import { Icon, type IconName } from '@gftg/ui';
import { Link, useLocation } from 'react-router';
import { useSavedSessions } from '../lib/savedSessions';

interface Tab {
  to: string;
  label: string;
  icon: IconName;
  /** Screens that belong to this tab without being under its path. */
  covers?: string[];
}

const TABS: Tab[] = [
  { to: '/', label: 'Home', icon: 'home' },
  // The venue mockup marks this tab, so the screens about the conference itself rather than about
  // this attendee belong to it: the venue, and the presenters by the same reading.
  { to: '/program', label: 'Agenda', icon: 'agenda', covers: ['/venue', '/speakers'] },
  { to: '/schedule', label: 'Schedule', icon: 'star' },
  // The design marks this tab while the directory is open: both screens are about people, and
  // the directory is reached from the home screen, so otherwise no tab would be lit at all.
  { to: '/matches', label: 'Matches', icon: 'matches', covers: ['/directory'] },
  { to: '/account', label: 'Profile', icon: 'profile' },
];

/**
 * The bar the design puts on every screen except the home screen and sign-in: home is the hub
 * and carries the footer instead, and sign-in has nowhere to navigate to yet.
 *
 * It is fixed to the viewport rather than the column, so it stays put while the agenda scrolls,
 * and constrained to the same width as the column so it lines up on a desktop window.
 */
export function TabBar() {
  const saved = useSavedSessions();
  const { pathname } = useLocation();

  return (
    <nav
      aria-label="Main"
      className="fixed bottom-0 left-1/2 z-20 w-full max-w-screen-sm -translate-x-1/2 border-t border-border-2 bg-surface pb-[env(safe-area-inset-bottom)]"
    >
      <ul className="grid grid-cols-5">
        {TABS.map((tab) => {
          const isActive = isTabActive(tab, pathname);

          return (
            <li key={tab.to}>
              <Link
                to={tab.to}
                aria-current={isActive ? 'page' : undefined}
                className="flex min-h-tabbar flex-col items-center justify-center gap-1"
              >
                <span className="relative">
                  <Icon
                    name={tab.icon}
                    size={23}
                    strokeWidth={isActive ? 2 : 1.5}
                    className={isActive ? 'text-ink' : 'text-ink-4'}
                  />
                  {tab.to === '/schedule' && saved.ids.size > 0 && (
                    <span className="absolute -top-1 -right-2.5 flex size-4 items-center justify-center rounded-full bg-brand text-[0.6rem] font-bold text-white tabular-nums">
                      {saved.ids.size}
                    </span>
                  )}
                </span>
                <span
                  className={`text-chip ${
                    isActive ? 'font-semibold text-ink' : 'font-medium text-ink-4'
                  }`}
                >
                  {tab.label}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function isTabActive(tab: Tab, pathname: string): boolean {
  if (tab.to === '/') {
    return pathname === '/';
  }

  return [tab.to, ...(tab.covers ?? [])].some(
    (path) => pathname === path || pathname.startsWith(`${path}/`),
  );
}
